import React, { useState, useEffect, useRef } from 'react';
import { useStore } from '../../store';
import {
  Key,
  Check,
  X,
  Eye,
  EyeOff,
  Loader2,
  Trash2,
  Cpu,
  MessageSquare,
  Palette,
  Image,
  Sparkles,
  FileText,
  Upload,
  AlertCircle,
  Bot,
  HelpCircle,
  Database,
  Shield,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { userApi, generateApi } from '../../services/api';
import { AI_PROVIDERS, ChatSettings } from '../../types';

interface SettingsPanelProps {
  onManageTemplates?: () => void;
  onManageDocumentTemplates?: () => void;
}

export default function SettingsPanel({
  onManageTemplates,
  onManageDocumentTemplates,
}: SettingsPanelProps) {
  const {
    applicantData,
    setApplicantData,
    systemPrompt,
    setSystemPrompt,
    brandingSettings,
    updateBrandingSettings,
    chatSettings,
    updateChatSettings,
    addNotification,
  } = useStore();

  const [apiKeyStatus, setApiKeyStatus] = useState<Record<string, boolean>>({});
  const [baseUrls, setBaseUrls] = useState<Record<string, string>>({});
  const [editingProvider, setEditingProvider] = useState<string | null>(null);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [baseUrlInput, setBaseUrlInput] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [savingKey, setSavingKey] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [generatingPrompt, setGeneratingPrompt] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    apiKeys: false,
    model: false,
    systemPrompt: false,
    branding: false,
    chat: false,
    templates: false,
    help: false,
    data: false,
    license: false,
  });
  const logoInputRef = useRef<HTMLInputElement>(null);

  const toggleSection = (key: string) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    fetchApiKeyStatus();
  }, []);

  const fetchApiKeyStatus = async () => {
    try {
      setLoadingStatus(true);
      const response = await userApi.getApiKeyStatus();
      setApiKeyStatus(response?.keys || {});
      setBaseUrls(response?.baseUrls || {});
    } catch {
      // Silently fail - keys just show as not configured
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleSaveApiKey = async (provider: string) => {
    const providerConfig = AI_PROVIDERS[provider];
    const keyValue = providerConfig.needsApiKey ? apiKeyInput.trim() : 'ollama';
    const urlValue = baseUrlInput.trim() || providerConfig.defaultBaseUrl || undefined;

    if (providerConfig.needsApiKey && !apiKeyInput.trim()) return;
    if (providerConfig.needsBaseUrl && !urlValue) return;

    setSavingKey(true);
    try {
      await userApi.setApiKey(provider, keyValue, urlValue);
      setApiKeyStatus((prev) => ({ ...prev, [provider]: true }));
      if (urlValue) {
        setBaseUrls((prev) => ({ ...prev, [provider]: urlValue }));
      }
      setEditingProvider(null);
      setApiKeyInput('');
      setBaseUrlInput('');
      setShowApiKey(false);
      addNotification(`${providerConfig.name} configuration saved`, 'success');
    } catch {
      addNotification(`Failed to save ${providerConfig.name} configuration`, 'error');
    } finally {
      setSavingKey(false);
    }
  };

  const handleDeleteApiKey = async (provider: string) => {
    try {
      await userApi.deleteApiKey(provider);
      setApiKeyStatus((prev) => ({ ...prev, [provider]: false }));
      setBaseUrls((prev) => {
        const updated = { ...prev };
        delete updated[provider];
        return updated;
      });
      addNotification(`${AI_PROVIDERS[provider].name} configuration removed`, 'success');
    } catch {
      addNotification(`Failed to remove ${AI_PROVIDERS[provider].name} configuration`, 'error');
    }
  };

  const handleGenerateSystemPrompt = async () => {
    setGeneratingPrompt(true);
    try {
      const result = await generateApi.generate({
        section: {
          id: 0,
          title: 'System Prompt',
          prompt: 'Generate a professional system prompt for document writing based on the provided applicant data and document context. The prompt should instruct the AI to write in a professional, detailed manner appropriate for the document type.',
        },
        applicantData,
        llmModel: applicantData.llmModel,
        systemPrompt: 'You are a prompt engineering expert. Generate a concise, effective system prompt for a document writing AI assistant. Return only the system prompt text, nothing else.',
      });
      if (result.content) {
        setSystemPrompt(result.content);
        addNotification('System prompt generated', 'success');
      }
    } catch {
      addNotification('Failed to generate system prompt', 'error');
    } finally {
      setGeneratingPrompt(false);
    }
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      addNotification('Please upload an image file', 'error');
      return;
    }

    // Resize image to prevent localStorage overflow
    const img = new window.Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const maxSize = 200;
      let { width, height } = img;
      if (width > maxSize || height > maxSize) {
        if (width > height) {
          height = (height / width) * maxSize;
          width = maxSize;
        } else {
          width = (width / height) * maxSize;
          height = maxSize;
        }
      }
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, width, height);
      const dataUrl = canvas.toDataURL('image/png');
      updateBrandingSettings({
        customLogoEnabled: true,
        logoPath: dataUrl,
      });
      addNotification('Logo uploaded', 'success');
    };
    img.src = URL.createObjectURL(file);

    // Reset input so the same file can be re-selected
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
  };

  const inputClass =
    'w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent bg-white';

  return (
    <div className="space-y-5">
      {/* API Key Management */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button
          onClick={() => toggleSection('apiKeys')}
          className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <h4 className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 uppercase tracking-wide">
            <Key size={12} />
            API Keys
          </h4>
          {expandedSections.apiKeys ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
        </button>

        {expandedSections.apiKeys && (
        <div className="p-3">
        {loadingStatus ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 size={16} className="animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="space-y-2">
            {Object.keys(AI_PROVIDERS).map((provider) => {
              const providerConfig = AI_PROVIDERS[provider];
              return (
              <div
                key={provider}
                className="border border-gray-200 rounded-lg p-2.5"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-700">
                      {providerConfig.name}
                    </span>
                    {apiKeyStatus[provider] ? (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                        <Check size={10} />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-1.5 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full">
                        Not set
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-1">
                    {apiKeyStatus[provider] && (
                      <button
                        onClick={() => handleDeleteApiKey(provider)}
                        className="p-1 text-gray-400 hover:text-red-600"
                        title="Remove"
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setEditingProvider(
                          editingProvider === provider ? null : provider
                        );
                        setApiKeyInput('');
                        setBaseUrlInput(baseUrls[provider] || providerConfig.defaultBaseUrl || '');
                        setShowApiKey(false);
                      }}
                      className="text-xs text-accent hover:text-accent-dark"
                    >
                      {editingProvider === provider
                        ? 'Cancel'
                        : apiKeyStatus[provider]
                        ? 'Update'
                        : 'Add'}
                    </button>
                  </div>
                </div>

                {editingProvider === provider && (
                  <div className="mt-2 space-y-2">
                    {providerConfig.needsBaseUrl && (
                      <input
                        type="text"
                        value={baseUrlInput}
                        onChange={(e) => setBaseUrlInput(e.target.value)}
                        placeholder={`Base URL (e.g. ${providerConfig.defaultBaseUrl || 'https://...'})`}
                        className={inputClass}
                      />
                    )}
                    {providerConfig.needsApiKey && (
                      <div className="relative">
                        <input
                          type={showApiKey ? 'text' : 'password'}
                          value={apiKeyInput}
                          onChange={(e) => setApiKeyInput(e.target.value)}
                          placeholder={`Enter ${providerConfig.name} API key...`}
                          className={inputClass + ' pr-8'}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveApiKey(provider);
                          }}
                        />
                        <button
                          onClick={() => setShowApiKey(!showApiKey)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showApiKey ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                      </div>
                    )}
                    <button
                      onClick={() => handleSaveApiKey(provider)}
                      disabled={
                        (providerConfig.needsApiKey && !apiKeyInput.trim()) ||
                        (providerConfig.needsBaseUrl && !baseUrlInput.trim() && !providerConfig.defaultBaseUrl) ||
                        savingKey
                      }
                      className="w-full flex items-center justify-center gap-1 px-3 py-1.5 text-xs bg-accent text-white rounded-md hover:bg-accent-dark disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {savingKey ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        <Check size={12} />
                      )}
                      Save
                    </button>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}
        </div>
        )}
      </div>

      {/* Model Selection */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button
          onClick={() => toggleSection('model')}
          className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <h4 className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 uppercase tracking-wide">
            <Cpu size={12} />
            AI Model
          </h4>
          {expandedSections.model ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
        </button>

        {expandedSections.model && (
        <div className="p-3 space-y-2">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Provider</label>
            <select
              value={applicantData.llmProvider}
              onChange={(e) =>
                setApplicantData({
                  llmProvider: e.target.value,
                  llmModel: '',
                })
              }
              className={inputClass}
            >
              {Object.keys(AI_PROVIDERS).map((provider) => (
                <option key={provider} value={provider}>
                  {AI_PROVIDERS[provider].name}
                  {!apiKeyStatus[provider] && AI_PROVIDERS[provider].needsApiKey ? ' (No key)' : ''}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">Model</label>
            <input
              type="text"
              value={applicantData.llmModel}
              onChange={(e) =>
                setApplicantData({ llmModel: e.target.value })
              }
              placeholder={AI_PROVIDERS[applicantData.llmProvider]?.placeholder || 'Enter model ID'}
              className={inputClass}
            />
          </div>

          {!apiKeyStatus[applicantData.llmProvider] && AI_PROVIDERS[applicantData.llmProvider]?.needsApiKey && (
            <div className="flex items-start gap-1.5 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
              <AlertCircle size={12} className="text-yellow-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-yellow-700">
                No API key configured for {AI_PROVIDERS[applicantData.llmProvider]?.name || applicantData.llmProvider}.
                Add one above to use this provider.
              </p>
            </div>
          )}
        </div>
        )}
      </div>

      {/* System Prompt */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button
          onClick={() => toggleSection('systemPrompt')}
          className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <h4 className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 uppercase tracking-wide">
            <MessageSquare size={12} />
            System Prompt
          </h4>
          {expandedSections.systemPrompt ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
        </button>

        {expandedSections.systemPrompt && (
        <div className="p-3 space-y-2">
          <textarea
            value={systemPrompt}
            onChange={(e) => setSystemPrompt(e.target.value)}
            className={inputClass + ' resize-none'}
            rows={4}
            placeholder="Enter a system prompt to guide AI generation across all sections..."
          />
          <button
            onClick={handleGenerateSystemPrompt}
            disabled={generatingPrompt}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs bg-accent/10 text-accent border border-accent/30 rounded-md hover:bg-accent/20 disabled:opacity-50 transition-colors"
          >
            {generatingPrompt ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Sparkles size={12} />
            )}
            {generatingPrompt ? 'Generating...' : 'Generate with AI'}
          </button>
        </div>
        )}
      </div>

      {/* Branding */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button
          onClick={() => toggleSection('branding')}
          className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <h4 className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 uppercase tracking-wide">
            <Palette size={12} />
            Branding
          </h4>
          {expandedSections.branding ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
        </button>

        {expandedSections.branding && (
        <div className="p-3 space-y-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1.5">Logo</label>
            <div className="flex items-center gap-3">
              {brandingSettings.customLogoEnabled && brandingSettings.logoPath ? (
                <div className="relative w-10 h-10 bg-gray-100 rounded-md overflow-hidden border border-gray-200">
                  <img
                    src={brandingSettings.logoPath}
                    alt="Logo"
                    className="w-full h-full object-contain"
                  />
                  <button
                    onClick={() =>
                      updateBrandingSettings({
                        customLogoEnabled: false,
                        logoPath: undefined,
                      })
                    }
                    className="absolute -top-1 -right-1 p-0.5 bg-red-500 text-white rounded-full"
                  >
                    <X size={8} />
                  </button>
                </div>
              ) : (
                <div className="w-10 h-10 bg-gray-100 rounded-md flex items-center justify-center border border-dashed border-gray-300">
                  <Image size={16} className="text-gray-400" />
                </div>
              )}
              <button
                onClick={() => logoInputRef.current?.click()}
                className="flex items-center gap-1 text-xs text-accent hover:text-accent-dark"
              >
                <Upload size={12} />
                {brandingSettings.logoPath ? 'Change' : 'Upload'}
              </button>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">Company Name</label>
            <input
              type="text"
              value={brandingSettings.companyName || ''}
              onChange={(e) =>
                updateBrandingSettings({ companyName: e.target.value })
              }
              className={inputClass}
              placeholder="Your company name"
            />
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1.5">Accent Color</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={brandingSettings.accentColor || '#3b4e6d'}
                  onChange={(e) =>
                    updateBrandingSettings({ accentColor: e.target.value })
                  }
                  className="w-9 h-9 rounded-md cursor-pointer border border-gray-200 p-0.5"
                />
                <input
                  type="text"
                  value={brandingSettings.accentColor || '#3b4e6d'}
                  onChange={(e) =>
                    updateBrandingSettings({ accentColor: e.target.value })
                  }
                  className="flex-1 px-2.5 py-1.5 text-xs border border-gray-200 rounded-md font-mono"
                  placeholder="#3b4e6d"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1.5">Button Text Color</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={brandingSettings.accentTextColor || '#ffffff'}
                  onChange={(e) =>
                    updateBrandingSettings({ accentTextColor: e.target.value })
                  }
                  className="w-9 h-9 rounded-md cursor-pointer border border-gray-200 p-0.5"
                />
                <input
                  type="text"
                  value={brandingSettings.accentTextColor || '#ffffff'}
                  onChange={(e) =>
                    updateBrandingSettings({ accentTextColor: e.target.value })
                  }
                  className="flex-1 px-2.5 py-1.5 text-xs border border-gray-200 rounded-md font-mono"
                  placeholder="#ffffff"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-md">
              <div
                className="w-16 h-7 rounded flex items-center justify-center text-xs font-medium"
                style={{
                  backgroundColor: brandingSettings.accentColor || '#3b4e6d',
                  color: brandingSettings.accentTextColor || '#ffffff',
                }}
              >
                Preview
              </div>
              <span className="text-xs text-gray-500">Button preview</span>
            </div>

            <button
              onClick={() => updateBrandingSettings({
                accentColor: '#3b4e6d',
                accentTextColor: '#ffffff',
                customLogoEnabled: false,
                logoPath: undefined,
                companyName: undefined,
              })}
              className="w-full mt-1 px-2.5 py-1.5 text-xs text-gray-500 border border-gray-200 rounded-md hover:bg-gray-50 hover:text-gray-700 transition-colors"
            >
              Reset to Default
            </button>
          </div>
        </div>
        )}
      </div>

      {/* Chat Assistant Settings */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button
          onClick={() => toggleSection('chat')}
          className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <h4 className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 uppercase tracking-wide">
            <Bot size={12} />
            Chat Assistant
          </h4>
          {expandedSections.chat ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
        </button>

        {expandedSections.chat && (
        <div className="p-3 space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-xs text-gray-600">Enable Chat</label>
            <button
              onClick={() =>
                updateChatSettings({ enabled: !chatSettings.enabled })
              }
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                chatSettings.enabled ? 'bg-accent' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                  chatSettings.enabled ? 'translate-x-4' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">Position</label>
            <select
              value={chatSettings.position}
              onChange={(e) =>
                updateChatSettings({
                  position: e.target.value as ChatSettings['position'],
                })
              }
              className={inputClass}
              disabled={!chatSettings.enabled}
            >
              <option value="bottom-right">Bottom Right</option>
              <option value="bottom-left">Bottom Left</option>
              <option value="top-right">Top Right</option>
              <option value="top-left">Top Left</option>
            </select>
          </div>

          <div className="flex items-center justify-between">
            <label className="text-xs text-gray-600">Save History</label>
            <button
              onClick={() =>
                updateChatSettings({ saveHistory: !chatSettings.saveHistory })
              }
              disabled={!chatSettings.enabled}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                chatSettings.saveHistory && chatSettings.enabled
                  ? 'bg-accent'
                  : 'bg-gray-300'
              } ${!chatSettings.enabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                  chatSettings.saveHistory && chatSettings.enabled
                    ? 'translate-x-4'
                    : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">
              Chat System Prompt
            </label>
            <textarea
              value={chatSettings.chatSystemPrompt || ''}
              onChange={(e) =>
                updateChatSettings({ chatSystemPrompt: e.target.value })
              }
              className={inputClass + ' resize-none'}
              rows={3}
              placeholder="Custom prompt for the chat assistant..."
              disabled={!chatSettings.enabled}
            />
          </div>
        </div>
        )}
      </div>

      {/* Template Management */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button
          onClick={() => toggleSection('templates')}
          className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <h4 className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 uppercase tracking-wide">
            <FileText size={12} />
            Templates
          </h4>
          {expandedSections.templates ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
        </button>

        {expandedSections.templates && (
        <div className="p-3 space-y-2">
          {onManageTemplates && (
            <button
              onClick={onManageTemplates}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 transition-colors"
            >
              <FileText size={12} />
              Manage Section Templates
            </button>
          )}
          {onManageDocumentTemplates && (
            <button
              onClick={onManageDocumentTemplates}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 transition-colors"
            >
              <FileText size={12} />
              Manage Document Templates
            </button>
          )}
        </div>
        )}
      </div>

      {/* Help & Support */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button
          onClick={() => toggleSection('help')}
          className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <h4 className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 uppercase tracking-wide">
            <HelpCircle size={12} />
            Help & Support
          </h4>
          {expandedSections.help ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
        </button>

        {expandedSections.help && (
        <div className="p-3 space-y-2">
          <div className="p-3 bg-accent/10 border border-accent/20 rounded-lg">
            <div className="flex items-start gap-2">
              <Bot size={16} className="text-accent mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-medium text-accent-dark mb-1">AI Help Assistant</p>
                <p className="text-xs text-accent mb-2">
                  Get answers about DocWriter features, templates, and more.
                </p>
                <button
                  onClick={() => {
                    const event = new CustomEvent('open-help-assistant');
                    window.dispatchEvent(event);
                  }}
                  className="text-xs font-medium text-accent hover:text-accent-dark underline"
                >
                  Open Help Assistant
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            {['Creating Templates', 'API Key Setup', 'Exporting Documents', 'Cloud Storage'].map((topic) => (
              <button
                key={topic}
                onClick={() => {
                  const event = new CustomEvent('open-help-assistant', { detail: { topic } });
                  window.dispatchEvent(event);
                }}
                className="p-2 text-xs text-gray-600 hover:text-accent bg-gray-50 hover:bg-accent/10 border border-gray-200 hover:border-accent/30 rounded-md transition-colors text-left"
              >
                {topic}
              </button>
            ))}
          </div>
        </div>
        )}
      </div>

      {/* Data Management */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button
          onClick={() => toggleSection('data')}
          className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <h4 className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 uppercase tracking-wide">
            <Database size={12} />
            Data Management
          </h4>
          {expandedSections.data ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
        </button>

        {expandedSections.data && (
        <div className="p-3 space-y-2">
          <button
            onClick={() => {
              if (window.confirm('Reset all templates to defaults? Custom templates will be removed.')) {
                addNotification('Templates reset to defaults', 'success');
              }
            }}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 transition-colors"
          >
            <FileText size={12} />
            Reset Templates
          </button>
          <button
            onClick={() => {
              if (window.confirm('Clear all form data? Templates will be kept.')) {
                useStore.getState().setGenericSections([]);
                addNotification('Form data cleared', 'success');
              }
            }}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 transition-colors"
          >
            <Trash2 size={12} />
            Clear Form Data
          </button>
          <button
            onClick={() => {
              if (window.confirm('Reset ALL data including templates? This cannot be undone.')) {
                useStore.getState().setGenericSections([]);
                addNotification('All data reset', 'success');
              }
            }}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 transition-colors"
          >
            <AlertCircle size={12} />
            Reset All Data
          </button>
        </div>
        )}
      </div>

      {/* License */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button
          onClick={() => toggleSection('license')}
          className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <h4 className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 uppercase tracking-wide">
            <Shield size={12} />
            License
          </h4>
          {expandedSections.license ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
        </button>

        {expandedSections.license && (
        <div className="p-3">
          <p className="text-xs text-gray-600 mb-2">
            Manage your DocWriter subscription and license.
          </p>
          <button
            onClick={() => {
              addNotification('License management coming soon', 'info');
            }}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-100 transition-colors"
          >
            <Shield size={12} />
            Manage License
          </button>
        </div>
        )}
      </div>
    </div>
  );
}
